import { useState, useEffect } from 'react';
import {
  Button,
  Table,
  Tag,
  Space,
  TimePicker,
  Row,
  Col,
    Card,
  Typography,
  Input,
  Select,
  message,
  Spin,
  Grid
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SaveOutlined, EditOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import type { TableProps } from 'antd';
import HorizontalWeekView from '@/components/common/HorizontalWeekView';
import { useAuth } from '@/contexts/AuthContext';
import { 
  getEmployees, 
  getAttendanceRecord, 
  createAttendanceRecord, 
  updateAttendanceRecord 
} from '@/api/employees';

const { Title } = Typography;
const { useBreakpoint } = Grid;

// Interface for the attendance table data
interface AttendanceTableData {
  id: string;
  employeeId: string;
  employeeName: string;
  checkInTime?: Dayjs;
  checkOutTime?: Dayjs;
  status: 'Present' | 'Absent' | 'Late' | 'On Leave' | 'Half Day';
}

export default function Attendance() {
  const { t } = useTranslation(['attendance', 'common', 'employees']);
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  
  // State
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [attendanceData, setAttendanceData] = useState<AttendanceTableData[]>([]);
  const [notes, setNotes] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [savingRecords, setSavingRecords] = useState<Set<string>>(new Set());
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingRows, setEditingRows] = useState<Set<string>>(new Set());
  const [hasExistingRecord, setHasExistingRecord] = useState(false);
  const [attendanceRecordId, setAttendanceRecordId] = useState<string | null>(null);

  // Queries
  const { data: employeesData, isLoading: employeesLoading } = useQuery({
    queryKey: ['employees', firestoreUser?.workspace?.id],
    queryFn: () => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return getEmployees({ 
        firestoreUser,
        pageSize: 100 // Get all employees
      });
    },
    enabled: !!firestoreUser?.workspace?.id,
    staleTime: 5 * 60 * 1000,
  });

  const { data: existingAttendance, isLoading: attendanceLoading } = useQuery({
    queryKey: ['attendance', selectedDate.date(), selectedDate.month() + 1, selectedDate.year(), firestoreUser?.workspace?.id],
    queryFn: () => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return getAttendanceRecord(
        selectedDate.date(),
        selectedDate.month() + 1, // dayjs months are 0-indexed
        selectedDate.year(),
        firestoreUser
      );
    },
    enabled: !!firestoreUser?.workspace?.id,
    staleTime: 5 * 60 * 1000,
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return createAttendanceRecord(data, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('attendance:messages.saveSuccess'));
      queryClient.invalidateQueries({ queryKey: ['attendance'] });
    },
    onError: (error: Error) => {
      message.error(error.message || t('attendance:messages.saveError'));
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return updateAttendanceRecord(id, data, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('attendance:messages.updateSuccess'));
      queryClient.invalidateQueries({ queryKey: ['attendance'] });
    },
    onError: (error: Error) => {
      message.error(error.message || t('attendance:messages.updateError'));
    }
  });

  // Initialize attendance data when employees or date changes
  useEffect(() => {
    if (!employeesData?.employees) return;
    
    // Check if we have existing attendance record for this date
    const hasRecord = !!existingAttendance;
    setHasExistingRecord(hasRecord);
    
    // Check if this is an auto-generated leave record
    const isAutoGeneratedLeave = existingAttendance?.notes?.includes('Auto-generated for approved leave request');
    
    if (hasRecord) {
      setAttendanceRecordId(existingAttendance.id);
      setNotes(existingAttendance.notes || '');
      
      if (isAutoGeneratedLeave) {
        // For auto-generated leave records, start in edit mode
        setIsEditMode(true);
        setEditingRows(new Set());
      } else {
        // For regular records, start in read-only mode
        setIsEditMode(false);
        setEditingRows(new Set());
      }
    } else {
      setAttendanceRecordId(null);
      setNotes('');
      // If no existing record, start in edit mode automatically
      setIsEditMode(true);
      setEditingRows(new Set());
    }
    
    // Create attendance data for all employees
    const newAttendanceData: AttendanceTableData[] = employeesData.employees.map(employee => {
      // Find existing employee record in the attendance
      const existingEmployeeRecord = existingAttendance?.employee?.find((emp: any) => 
        emp.id === employee.id
      );

      if (existingEmployeeRecord) {
        return {
          id: employee.id,
          employeeId: employee.id,
          employeeName: employee.user.displayName,
          checkInTime: existingEmployeeRecord.checkInTime ? dayjs(existingEmployeeRecord.checkInTime.toDate()) : undefined,
          checkOutTime: existingEmployeeRecord.checkOutTime ? dayjs(existingEmployeeRecord.checkOutTime.toDate()) : undefined,
          status: existingEmployeeRecord.status || 'Absent'
        };
      } else {
        return {
          id: employee.id,
          employeeId: employee.id,
          employeeName: employee.user.displayName,
          checkInTime: undefined,
          checkOutTime: undefined,
          status: 'Absent'
        };
      }
    });

    setAttendanceData(newAttendanceData);
  }, [employeesData, existingAttendance, selectedDate]);

  const getStatusColor = (status: AttendanceTableData['status']) => {
    switch (status) {
      case 'Present': return 'success';
      case 'Absent': return 'error';
      case 'Late': return 'warning';
      case 'On Leave': return 'processing';
      case 'Half Day': return 'orange';
      default: return 'default';
    }
  };

  const handleDateChange = (date: Dayjs) => {
    setSelectedDate(date);
  };

  const handleTimeChange = (recordId: string, field: 'checkInTime' | 'checkOutTime', value: Dayjs | null) => {
    setAttendanceData(prev => 
      prev.map(record => 
        record.id === recordId 
          ? { ...record, [field]: value }
          : record
      )
    );
  };

  const handleStatusChange = (recordId: string, status: AttendanceTableData['status']) => {
    setAttendanceData(prev => 
      prev.map(record => 
        record.id === recordId 
          ? { ...record, status }
          : record
      )
    );
  };

  const handleEditRow = (recordId: string) => {
    setEditingRows(prev => new Set(prev).add(recordId));
  };

  const handleEditAll = () => {
    setIsEditMode(true);
    setEditingRows(new Set());
  };

  const handleSaveRecord = async (record: AttendanceTableData) => {
    setSavingRecords(prev => new Set(prev).add(record.id));
    
    try {
      // Update the specific employee in the attendance data
      const updatedAttendanceData = attendanceData.map(r => 
        r.id === record.id ? record : r
      );

      const employeeData = updatedAttendanceData.map(emp => ({
        id: emp.employeeId,
        name: emp.employeeName,
        checkInTime: emp.checkInTime?.toDate(),
        checkOutTime: emp.checkOutTime?.toDate(),
        status: emp.status
      }));

      const data = {
        employee: employeeData,
        day: selectedDate.date(),
        month: selectedDate.month() + 1, // dayjs months are 0-indexed
        year: selectedDate.year(),
        notes: notes
      };

      if (attendanceRecordId) {
        await updateMutation.mutateAsync({ id: attendanceRecordId, data });
      } else {
        await createMutation.mutateAsync(data);
      }

      // Exit edit mode for this row
      setEditingRows(prev => {
        const newSet = new Set(prev);
        newSet.delete(record.id);
        return newSet;
      });
    } catch (error) {
      console.error('Error saving record:', error);
    } finally {
      setSavingRecords(prev => {
        const newSet = new Set(prev);
        newSet.delete(record.id);
        return newSet;
      });
    }
  };

  const handleSaveAll = async () => {
    setIsSaving(true);
    try {
      const employeeData = attendanceData.map(emp => ({
        id: emp.employeeId,
        name: emp.employeeName,
        checkInTime: emp.checkInTime?.toDate(),
        checkOutTime: emp.checkOutTime?.toDate(),
        status: emp.status
      }));

      const data = {
        employee: employeeData,
        day: selectedDate.date(),
        month: selectedDate.month() + 1, // dayjs months are 0-indexed
        year: selectedDate.year(),
        notes: notes
      };

      if (attendanceRecordId) {
        await updateMutation.mutateAsync({ id: attendanceRecordId, data });
      } else {
        await createMutation.mutateAsync(data);
      }
      
      message.success(t('attendance:messages.saveSuccess'));
      
      // Exit edit mode
      setIsEditMode(false);
      setEditingRows(new Set());
    } catch (error) {
      console.error('Error saving attendance:', error);
      message.error(t('attendance:messages.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const isRowEditable = (recordId: string) => {
    // Check if this is an auto-generated leave record
    const isAutoGeneratedLeave = existingAttendance?.notes?.includes('Auto-generated for approved leave request');
    
    if (isAutoGeneratedLeave) {
      // For auto-generated leave records, find the employee on leave
      const leaveEmployee = existingAttendance?.employee?.find((emp: any) => emp.status === 'On Leave');
      
      // If this is the employee on leave, make them read-only
      if (leaveEmployee && leaveEmployee.id === recordId) {
        return false;
      }
    }
    
    return isEditMode || editingRows.has(recordId);
  };

  const columns: TableProps<AttendanceTableData>['columns'] = [
    {
      title: t('employees:fields.name'),
      dataIndex: 'employeeName',
      key: 'employeeName',
      width: 200,
      fixed: 'left' as const,
    },
    {
      title: t('attendance:checkIn'),
      dataIndex: 'checkInTime',
      key: 'checkInTime',
      width: 120,
      render: (_, record) => {
        const editable = isRowEditable(record.id);
        return editable ? (
          <TimePicker
            format="HH:mm"
            value={record.checkInTime}
            onChange={(value) => handleTimeChange(record.id, 'checkInTime', value)}
            placeholder="--:--"
            style={{ width: '100%' }}
          />
        ) : (
          <span>{record.checkInTime ? record.checkInTime.format('HH:mm') : '--:--'}</span>
        );
      },
    },
    {
      title: t('attendance:checkOut'),
      dataIndex: 'checkOutTime',
      key: 'checkOutTime',
      width: 120,
      render: (_, record) => {
        const editable = isRowEditable(record.id);
        return editable ? (
          <TimePicker
            format="HH:mm"
            value={record.checkOutTime}
            onChange={(value) => handleTimeChange(record.id, 'checkOutTime', value)}
            placeholder="--:--"
            style={{ width: '100%' }}
          />
        ) : (
          <span>{record.checkOutTime ? record.checkOutTime.format('HH:mm') : '--:--'}</span>
        );
      },
    },
    {
      title: t('common:statusLabel'),
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (status: AttendanceTableData['status'], record) => {
        const editable = isRowEditable(record.id);
        return editable ? (
          <Select
            value={status}
            onChange={(value) => handleStatusChange(record.id, value)}
            style={{ width: '100%' }}
          >
            <Select.Option value="Present">{t('attendance:status.present')}</Select.Option>
            <Select.Option value="Absent">{t('attendance:status.absent')}</Select.Option>
            <Select.Option value="Late">{t('attendance:status.late')}</Select.Option>
            <Select.Option value="On Leave">{t('attendance:status.onleave')}</Select.Option>
            <Select.Option value="Half Day">{t('attendance:status.halfday')}</Select.Option>
          </Select>
        ) : (
          <Tag color={getStatusColor(status)}>
            {t(`attendance:status.${status.toLowerCase().replace(' ', '')}`)}
          </Tag>
        );
      },
    },
    {
      title: t('common:actions.title'),
      key: 'actions',
      width: 120,
      fixed: 'right' as const,
      render: (_, record) => {
        const editable = isRowEditable(record.id);
        return editable ? (
          <Button
            type="primary"
            icon={<SaveOutlined />}
            size="small"
            loading={savingRecords.has(record.id)}
            onClick={() => handleSaveRecord(record)}
          >
            {t('common:actions.save')}
          </Button>
        ) : (
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEditRow(record.id)}
          >
            {t('common:actions.edit')}
          </Button>
        );
      },
    },
  ];

  const renderMobileCard = (record: AttendanceTableData) => {
    const editable = isRowEditable(record.id);
    
    return (
      <Card
        key={record.id}
        size="small"
        style={{ marginBottom: 16 }}
        title={
          <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
            {record.employeeName}
          </div>
        }
        extra={
          <Space>
            <Tag color={getStatusColor(record.status)}>
              {t(`attendance:status.${record.status.toLowerCase().replace(' ', '')}`)}
            </Tag>
            {!editable && (
              <Button
                icon={<EditOutlined />}
                size="small"
                onClick={() => handleEditRow(record.id)}
              >
                {t('common:actions.edit')}
              </Button>
            )}
          </Space>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {/* Status */}
          <div>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>
              {t('common:statusLabel')}:
            </div>
            {editable ? (
              <Select
                value={record.status}
                onChange={(value) => handleStatusChange(record.id, value)}
                style={{ width: '100%' }}
              >
                <Select.Option value="Present">{t('attendance:status.present')}</Select.Option>
                <Select.Option value="Absent">{t('attendance:status.absent')}</Select.Option>
                <Select.Option value="Late">{t('attendance:status.late')}</Select.Option>
                <Select.Option value="On Leave">{t('attendance:status.onleave')}</Select.Option>
                <Select.Option value="Half Day">{t('attendance:status.halfday')}</Select.Option>
              </Select>
            ) : (
              <Tag color={getStatusColor(record.status)}>
                {t(`attendance:status.${record.status.toLowerCase().replace(' ', '')}`)}
              </Tag>
            )}
          </div>

          {/* Time Pickers */}
          <Row gutter={16}>
            <Col span={12}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>
                {t('attendance:checkIn')}:
              </div>
              {editable ? (
                <TimePicker
                  format="HH:mm"
                  value={record.checkInTime}
                  onChange={(value) => handleTimeChange(record.id, 'checkInTime', value)}
                  placeholder="--:--"
                  style={{ width: '100%' }}
                />
              ) : (
                <div>{record.checkInTime ? record.checkInTime.format('HH:mm') : '--:--'}</div>
              )}
            </Col>
            <Col span={12}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>
                {t('attendance:checkOut')}:
              </div>
              {editable ? (
                <TimePicker
                  format="HH:mm"
                  value={record.checkOutTime}
                  onChange={(value) => handleTimeChange(record.id, 'checkOutTime', value)}
                  placeholder="--:--"
                  style={{ width: '100%' }}
                />
              ) : (
                <div>{record.checkOutTime ? record.checkOutTime.format('HH:mm') : '--:--'}</div>
              )}
            </Col>
          </Row>

          {/* Save Button */}
          {editable && (
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={savingRecords.has(record.id)}
              onClick={() => handleSaveRecord(record)}
              style={{ width: '100%' }}
            >
              {t('common:actions.save')}
            </Button>
          )}
        </Space>
      </Card>
    );
  };

  if (employeesLoading || attendanceLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* Header */}
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={3}>{t('attendance:title')}</Title>
          </Col>
          <Col>
            <Space>
              {hasExistingRecord && !isEditMode && (
                <Button
                  icon={<EditOutlined />}
                  onClick={handleEditAll}
                >
                  {t('attendance:editAll')}
                </Button>
              )}
              {isEditMode && (
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleSaveAll}
                  loading={isSaving}
                >
                  {t('attendance:saveAll')}
                </Button>
              )}
            </Space>
          </Col>
        </Row>

        {/* Date Selection */}
        <Card>
          <HorizontalWeekView
            selectedDate={selectedDate}
            onSelectDate={handleDateChange}
          />
        </Card>

        {/* Attendance Content */}
        <Card 
          title={
            <Space>
              <span>{t('attendance:attendanceFor')} {selectedDate.format('DD MMMM YYYY')}</span>
              {existingAttendance?.notes?.includes('Auto-generated for approved leave request') && (
                <Tag color="blue">{t('attendance:autoGeneratedLeave')}</Tag>
              )}
            </Space>
          }
        >
          {isMobile ? (
            // Mobile Card View
            <div>
              {attendanceData.map(renderMobileCard)}
            </div>
          ) : (
            // Desktop Table View
            <Table
              columns={columns}
              dataSource={attendanceData}
              rowKey="id"
              pagination={false}
              scroll={{ x: 900 }}
              size="small"
            />
          )}
        </Card>

        {/* General Notes */}
        <Card title={t('attendance:generalNotes')}>
          <Input.TextArea
            rows={4}
            value={notes}
            onChange={(e: any) => setNotes(e.target.value)}
            placeholder={t('attendance:generalNotesPlaceholder')}
            disabled={!isEditMode && hasExistingRecord}
          />
        </Card>
      </Space>
    </div>
  );
} 